import React from 'react';
import styles from './Skeleton.module.scss';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 16, borderRadius = 6, className }) => (
  <div className={`${styles.skeleton} ${className ?? ''}`} style={{ width, height, borderRadius }} />
);

/** Predefined skeleton variants */

export const SkeletonText: React.FC<{ width?: string | number; lines?: number }> = ({ width = '100%', lines = 1 }) => (
  <div className={styles.textGroup}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} width={i === lines - 1 && lines > 1 ? '60%' : width} height={14} />
    ))}
  </div>
);

export const SkeletonCircle: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <Skeleton width={size} height={size} borderRadius="50%" />
);

export const SkeletonCard: React.FC<{ height?: number }> = ({ height = 120 }) => (
  <Skeleton width="100%" height={height} borderRadius={12} />
);
const HEADER_BAR_WIDTHS = [72, 88, 64, 92, 76, 84, 68];
const CELL_BAR_WIDTHS = [82, 58, 74, 66, 88, 62, 78];

const TAB_BAR_WIDTHS = [96, 112, 84, 104, 92, 118];

/** Table skeleton — mimics the Table component's skeleton rows */
export const SkeletonTable: React.FC<{ columns?: number; rows?: number }> = ({ columns = 5, rows = 6 }) => (
  <div className={styles.table}>
    <div className={styles.tableHeader}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} height={12} width={`${HEADER_BAR_WIDTHS[i % HEADER_BAR_WIDTHS.length]}%`} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <div key={rowIdx} className={styles.tableRow}>
        {Array.from({ length: columns }).map((_, colIdx) => (
          <Skeleton
            key={colIdx}
            height={14}
            width={`${CELL_BAR_WIDTHS[(rowIdx + colIdx) % CELL_BAR_WIDTHS.length]}%`}
          />
        ))}
      </div>
    ))}
  </div>
);

/** List page skeleton — header + filters + table */
export const ListPageSkeleton: React.FC<{
  showFilters?: boolean;
  filterCount?: number;
  columns?: number;
  rows?: number;
}> = ({ showFilters = true, filterCount = 2, columns = 5, rows = 6 }) => (
  <div className={styles.listPage} data-testid="list-page-skeleton">
    <div className={styles.listHeader}>
      <Skeleton width={180} height={28} borderRadius={8} />
      <Skeleton width={120} height={36} borderRadius={8} />
    </div>
    {showFilters && (
      <div className={styles.listFilters}>
        {Array.from({ length: filterCount }).map((_, i) => (
          <Skeleton key={i} width={i === 0 ? 280 : 160} height={40} borderRadius={8} />
        ))}
      </div>
    )}
    <SkeletonTable columns={columns} rows={rows} />
  </div>
);

/** Grid page skeleton — header + filters + card grid */
export const GridPageSkeleton: React.FC<{
  showFilters?: boolean;
  filterCount?: number;
  cardCount?: number;
  cardHeight?: number;
}> = ({ showFilters = false, filterCount = 2, cardCount = 8, cardHeight = 180 }) => (
  <div className={styles.listPage} data-testid="grid-page-skeleton">
    <div className={styles.listHeader}>
      <Skeleton width={180} height={28} borderRadius={8} />
      <Skeleton width={120} height={36} borderRadius={8} />
    </div>
    {showFilters && (
      <div className={styles.listFilters}>
        {Array.from({ length: filterCount }).map((_, i) => (
          <Skeleton key={i} width={i === 0 ? 280 : 160} height={40} borderRadius={8} />
        ))}
      </div>
    )}
    <div className={styles.gridCards}>
      {Array.from({ length: cardCount }).map((_, i) => (
        <SkeletonCard key={i} height={cardHeight} />
      ))}
    </div>
  </div>
);

/** Detail page skeleton — breadcrumb + header + tabs + content cards */
export const DetailPageSkeleton: React.FC<{
  tabCount?: number;
}> = ({ tabCount = 5 }) => (
  <div className={styles.detailPage}>
    {/* Breadcrumb */}
    <div className={styles.detailBreadcrumb}>
      <Skeleton width={60} height={14} />
      <Skeleton width={8} height={14} borderRadius={2} />
      <Skeleton width={140} height={14} />
    </div>

    {/* Header */}
    <div className={styles.detailHeader}>
      <div className={styles.detailHeaderLeft}>
        <Skeleton width={300} height={32} borderRadius={8} />
        <Skeleton width={180} height={16} />
      </div>
    </div>

    {/* Tabs */}
    <div className={styles.detailTabs}>
      {Array.from({ length: tabCount }).map((_, i) => (
        <Skeleton key={i} width={TAB_BAR_WIDTHS[i % TAB_BAR_WIDTHS.length]} height={36} borderRadius={8} />
      ))}
    </div>

    {/* Content cards */}
    <div className={styles.detailContent}>
      <SkeletonCard height={240} />
      <SkeletonCard height={240} />
    </div>
  </div>
);

/** Dashboard skeleton — banner + stat cards + content */
export const DashboardSkeleton: React.FC<{ statCardCount?: number }> = ({ statCardCount = 4 }) => (
  <div className={styles.dashboardPage}>
    {/* Welcome banner */}
    <SkeletonCard height={120} />

    {/* Stat cards */}
    <div className={styles.dashboardStats}>
      {Array.from({ length: statCardCount }).map((_, i) => (
        <SkeletonCard key={i} height={110} />
      ))}
    </div>

    {/* Content */}
    <div className={styles.dashboardContent}>
      <SkeletonCard height={300} />
      <SkeletonCard height={300} />
    </div>
  </div>
);

export default Skeleton;
